'use client'

import React, { useState } from 'react'
import { CategoryFaq } from '@/services/dataTypes'
import { HelpCircle, ChevronDown, Sparkles, MessageCircle, ArrowRight } from 'lucide-react'
import Link from 'next/link'

interface FAQItem {
    id?: number | string
    question: string
    answer: string
}

interface Props {
    faqs: FAQItem[] | CategoryFaq[]
    categoryName?: string
    heading?: string
    text?: string
}

function FAQAccordionCard({
    faq,
    index,
    isOpen,
    onToggle
}: {
    faq: FAQItem | CategoryFaq
    index: number
    isOpen: boolean
    onToggle: () => void
}) {
    return (
        <div
            className={`rounded-xl border transition-all duration-300 overflow-hidden ${
                isOpen
                    ? 'bg-white border-[#5C7A5C]/40 shadow-[0_4px_16px_rgba(45,62,45,0.07)]'
                    : 'bg-white/90 hover:bg-white border-[#5C7A5C]/15 hover:border-[#5C7A5C]/30 shadow-[0_1px_6px_rgba(45,62,45,0.03)]'
            }`}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
        >
            {/* Question Trigger */}
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-transparent border-none cursor-pointer group"
                aria-expanded={isOpen}
            >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Index Badge */}
                    <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-300 font-bold text-[11px] ${
                            isOpen
                                ? 'bg-[#F5C542] text-[#2D3E2D] shadow-sm'
                                : 'bg-[#FAF7F2] text-[#5C7A5C] border border-[#5C7A5C]/15 group-hover:bg-[#E8F0E8]'
                        }`}
                    >
                        {String(index + 1).padStart(2, '0')}
                    </div>

                    {/* Question Title */}
                    <span
                        className={`text-xs sm:text-sm font-bold transition-colors duration-200 leading-snug ${
                            isOpen ? 'text-[#2D3E2D]' : 'text-[#2D3E2D]/85 group-hover:text-[#2D3E2D]'
                        }`}
                        style={{ fontFamily: 'Georgia, serif' }}
                        itemProp="name"
                    >
                        {faq.question}
                    </span>
                </div>

                {/* Chevron */}
                <div
                    className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 transition-all duration-300 ${
                        isOpen ? 'bg-[#2D3E2D] text-[#FAF7F2]' : 'bg-[#FAF7F2] text-[#2D3E2D]/40 group-hover:text-[#2D3E2D]'
                    }`}
                >
                    <ChevronDown
                        size={13}
                        className="transition-transform duration-300"
                        style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    />
                </div>
            </button>

            {/* Answer */}
            <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
            >
                <div className="overflow-hidden">
                    <div className="px-4 pb-4 pt-1 border-t border-[#5C7A5C]/10">
                        <div
                            className="text-xs sm:text-sm text-[#2D3E2D]/75 leading-relaxed prose prose-sm max-w-none pt-2"
                            itemProp="text"
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

const CategoryFAQs = ({ faqs, categoryName, heading, text }: Props) => {
    const [openIndex, setOpenIndex] = useState<number | null>(0)

    if (!faqs?.length) return null

    const handleToggle = (index: number) => {
        setOpenIndex(prev => (prev === index ? null : index))
    }

    return (
        <section
            className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 relative"
            aria-labelledby="cat-faq-heading"
            itemScope
            itemType="https://schema.org/FAQPage"
        >
            <div className="max-w-4xl mx-auto">
                {/* ── Section Header ── */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-5 sm:mb-7 pb-4 border-b border-[#5C7A5C]/15">
                    <div>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-2 bg-[#E8F0E8] border border-[#5C7A5C]/20">
                            <Sparkles size={11} className="text-[#5C7A5C]" />
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#2D3E2D]">
                                {categoryName ? `${categoryName} · FAQ` : 'Help & Answers'}
                            </span>
                        </div>

                        <h2
                            id="cat-faq-heading"
                            className="text-xl sm:text-2xl font-black text-[#2D3E2D] tracking-tight"
                            style={{ fontFamily: 'Georgia, serif' }}
                        >
                            {heading || (
                                <>
                                    Frequently Asked{' '}
                                    <span className="font-normal italic" style={{ color: '#5C7A5C' }}>
                                        Questions
                                    </span>
                                </>
                            )}
                        </h2>

                        <p className="text-xs text-[#2D3E2D]/65 mt-1 max-w-xl">
                            {text || 'Everything you need to know about discounts, promo codes, and merchant offers.'}
                        </p>
                    </div>

                    <Link
                        href="/contact-us"
                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold text-[#2D3E2D] bg-white hover:bg-[#2D3E2D] hover:text-white border border-[#5C7A5C]/20 hover:border-[#2D3E2D] transition-all duration-200 shadow-2xs self-start md:self-auto shrink-0 group"
                    >
                        <MessageCircle size={13} className="text-[#5C7A5C] group-hover:text-white transition-colors" />
                        <span>Have a Question?</span>
                        <ArrowRight size={11} className="text-[#F5C542] transition-transform group-hover:translate-x-0.5" />
                    </Link>
                </div>

                {/* ── FAQ Accordion Stack ── */}
                <div className="space-y-2">
                    {faqs.map((faq, i) => (
                        <FAQAccordionCard
                            key={faq.id ?? i}
                            faq={faq}
                            index={i}
                            isOpen={openIndex === i}
                            onToggle={() => handleToggle(i)}
                        />
                    ))}
                </div>

                {/* ── Bottom Help Banner ── */}
                <div className="mt-5 p-4 rounded-xl bg-[#FAF7F2] border border-[#5C7A5C]/15 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white border border-[#5C7A5C]/20 flex items-center justify-center shrink-0 text-[#5C7A5C] shadow-2xs">
                            <HelpCircle size={16} />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-[#2D3E2D]">
                                Still couldn&apos;t find what you are looking for?
                            </p>
                            <p className="text-[11px] text-[#2D3E2D]/55">
                                Our support team is always available to assist with broken coupons or store requests.
                            </p>
                        </div>
                    </div>
                    <Link
                        href="/contact-us"
                        className="text-xs font-bold text-[#5C7A5C] hover:text-[#2D3E2D] underline underline-offset-4 shrink-0 transition-colors"
                    >
                        Contact Support Team →
                    </Link>
                </div>
            </div>
        </section>
    )
}

export default CategoryFAQs

